import {
  Button,
  Checkbox,
  HeartHandshakeIcon,
  IdCardIcon,
  TextInput,
  Typography,
} from "@/components/atoms";
import { OkDialog, ProgressHeader } from "@/components/molecules";
import { NationalityDropdown } from "@/components/molecules/NationalityDropdown";
import { useTranslation, useUIColors } from "@/core/hooks";
import { logger } from "@/core/logger";
import { trackDynatraceEvent } from "@/core/services/dynatraceService";
import { getNationalities, type Country } from "@/core/utils/countryCodes";
import {
  formatEmiratesId,
  validateEmiratesId,
  validateName,
  validateOptionalName,
  validatePassportNumber,
} from "@/core/utils/validation";
import { router } from "expo-router";
import React, { useEffect, useRef, useState } from "react";
import {
  Dimensions,
  Keyboard,
  KeyboardAvoidingView,
  Platform,
  Text as RNText,
  TextInput as RNTextInput,
  ScrollView,
  StyleSheet,
  View,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import {
  REGISTRATION_STEP,
  REGISTRATION_TYPE,
  useManualRegistrationStore,
  type RegistrationType,
} from "./store/registrationStore";
import type { RegisterErrorResponse } from "./types";
import { useRegistrationProgress } from "./utils/registrationProgress";

interface RegistrationFormScreenProps {
  registrationType?: RegistrationType;
}

export function RegistrationFormScreen({
  registrationType: propRegistrationType,
}: RegistrationFormScreenProps) {
  const colors = useUIColors();
  const { t } = useTranslation();
  const insets = useSafeAreaInsets();
  const registrationStore = useManualRegistrationStore();
  const progress = useRegistrationProgress();

  // Determine registration type from prop or store
  const registrationType: RegistrationType | undefined =
    propRegistrationType || registrationStore.registrationType || undefined;
  const agencyType =
    registrationType === REGISTRATION_TYPE.EMIRATES_ID
      ? "domestic"
      : registrationType === REGISTRATION_TYPE.PASSPORT
        ? "international"
        : undefined;
  const registrationSectionName =
    agencyType === "domestic"
      ? "domesticAgencyRegistration"
      : agencyType === "international"
        ? "internationalAgencyRegistration"
        : undefined;

  // Loading and dialog state
  const [isLoading, setIsLoading] = useState(false);
  const [errorDialogVisible, setErrorDialogVisible] = useState(false);
  const [errorDialogMessage, setErrorDialogMessage] = useState("");
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);

  // Check if registration was already successful (fields should be permanently disabled)
  const isRegistrationSuccessful =
    registrationStore.registrationResponse !== null;

  // Fields should be disabled when loading or after successful registration
  // Form is always editable before successful registration
  const areFieldsDisabled = isLoading || isRegistrationSuccessful;

  // Form state
  const [firstName, setFirstName] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [lastName, setLastName] = useState("");
  const [emiratesId, setEmiratesId] = useState("");
  const [passportNumber, setPassportNumber] = useState("");
  const [selectedNationality, setSelectedNationality] = useState<
    Country | undefined
  >(undefined);

  // Load saved state on mount
  useEffect(() => {
    if (!registrationType) {
      logger.warn("RegistrationFormScreen", "Registration type not set");
      return;
    }

    // If we have saved data for this registration type, restore it
    if (
      registrationStore.formData &&
      registrationStore.registrationType === registrationType
    ) {
      const saved = registrationStore.formData;
      setFirstName(saved.firstName || "");
      setMiddleName(saved.middleName || "");
      setLastName(saved.lastName || "");
      setEmiratesId(saved.emiratesId || "");
      setPassportNumber(saved.passportNumber || "");

      // Restore nationality
      if (saved.nationality) {
        const nationalities = getNationalities();
        const nationality = nationalities.find(
          (n) => n.nationality === saved.nationality,
        );
        if (nationality) {
          setSelectedNationality(nationality);
        }
      }

      logger.info("RegistrationFormScreen", "Restored saved form data", {
        registrationType,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registrationType]); // Run when registration type is determined

  // Error state
  const [errors, setErrors] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    emiratesId: "",
    passportNumber: "",
    nationality: "",
  });

  // Touched state (to show errors only after user interacts)
  const [touched, setTouched] = useState({
    firstName: false,
    middleName: false,
    lastName: false,
    emiratesId: false,
    passportNumber: false,
    nationality: false,
  });

  // Refs for keyboard navigation
  const firstNameRef = useRef<RNTextInput>(null);
  const middleNameRef = useRef<RNTextInput>(null);
  const lastNameRef = useRef<RNTextInput>(null);
  const emiratesIdRef = useRef<RNTextInput>(null);
  const passportNumberRef = useRef<RNTextInput>(null);
  const scrollViewRef = useRef<ScrollView>(null);

  // Store field positions for scrolling
  const fieldPositions = useRef<Record<string, number>>({});
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const screenHeight = Dimensions.get("window").height;

  // Listen to keyboard events to get keyboard height
  useEffect(() => {
    const showSubscription = Keyboard.addListener(
      Platform.OS === "ios" ? "keyboardWillShow" : "keyboardDidShow",
      (e) => {
        setKeyboardHeight(e.endCoordinates.height);
      },
    );
    const hideSubscription = Keyboard.addListener(
      Platform.OS === "ios" ? "keyboardWillHide" : "keyboardDidHide",
      () => {
        setKeyboardHeight(0);
      },
    );

    return () => {
      showSubscription.remove();
      hideSubscription.remove();
    };
  }, []);

  useEffect(() => {
    trackDynatraceEvent({
      p_screen: "signup",
      p_sectionName: registrationSectionName,
      p_agencyType: agencyType,
      p_actionName: "Registration Screen Viewed",
      eventType: "view",
    });
  }, [registrationSectionName, agencyType]);

  const handleBackPress = () => {
    router.back();
  };

  // Scroll to field when focused
  const handleFieldFocus = (fieldName: string) => {
    const fieldY = fieldPositions.current[fieldName];
    if (fieldY !== undefined && scrollViewRef.current) {
      // Use setTimeout to ensure keyboard animation has started
      setTimeout(
        () => {
          if (scrollViewRef.current) {
            // Calculate available visible area when keyboard is shown
            // Screen height - keyboard - button area - header area
            const headerHeight = 120; // Header + progress bar
            const buttonArea = Math.max(insets.bottom, 45) + 80; // Button height + padding
            const currentKeyboardHeight = keyboardHeight || 300; // Fallback if keyboard height not yet set
            const availableHeight =
              screenHeight - currentKeyboardHeight - buttonArea - headerHeight;

            // Always scroll to show field with generous padding above keyboard
            // This ensures field is fully visible, not just partially
            const paddingAboveField = 80; // Padding to show field clearly above keyboard
            const scrollOffset = fieldY - availableHeight + paddingAboveField;

            scrollViewRef.current.scrollTo({
              y: Math.max(0, scrollOffset),
              animated: true,
            });
          }
        },
        Platform.OS === "ios" ? 350 : 200,
      ); // Wait for keyboard animation to complete
    }
  };

  // Store field position on layout (relative to ScrollView content)
  const handleFieldLayout = (fieldName: string, event: any) => {
    const { y } = event.nativeEvent.layout;
    // Store Y position relative to the ScrollView content container
    fieldPositions.current[fieldName] = y;
  };

  // Validation functions
  const validateField = (
    fieldName: keyof typeof errors,
    value: string | Country | undefined,
  ) => {
    let error = "";

    switch (fieldName) {
      case "firstName":
        error = validateName(value as string);
        break;
      case "middleName":
        error = validateOptionalName(value as string);
        break;
      case "lastName":
        error = validateName(value as string);
        break;
      case "emiratesId":
        error = validateEmiratesId(value as string);
        break;
      case "passportNumber":
        error = validatePassportNumber(value as string);
        break;
      case "nationality":
        if (!value) {
          error = "Nationality is required";
        }
        break;
    }

    setErrors((prev) => ({ ...prev, [fieldName]: error }));
    return error === "";
  };

  // Map API field names to form field names
  const mapApiFieldToFormField = (
    apiField: string,
  ): keyof typeof errors | null => {
    const fieldMap: Record<string, keyof typeof errors> = {
      first_name: "firstName",
      middle_name: "middleName",
      last_name: "lastName",
      emirates_id: "emiratesId",
      passport_number: "passportNumber",
      nationality: "nationality",
    };
    return fieldMap[apiField] || null;
  };

  // Map API errors to form errors
  const mapApiErrorsToFormErrors = (
    apiErrors: RegisterErrorResponse["errors"] = [],
  ) => {
    const fieldsToTouch: (keyof typeof errors)[] = [];
    setErrors((prev) => {
      const newErrors = { ...prev };
      apiErrors.forEach((error) => {
        const formField = mapApiFieldToFormField(error.field);
        if (formField) {
          newErrors[formField] = error.message;
          fieldsToTouch.push(formField);
        }
      });
      return newErrors;
    });
    // Mark fields as touched so errors are displayed
    if (fieldsToTouch.length > 0) {
      setTouched((prev) => {
        const newTouched = { ...prev };
        fieldsToTouch.forEach((field) => {
          newTouched[field] = true;
        });
        return newTouched;
      });
    }
  };

  // Validate all fields
  const validateAllFields = (): boolean => {
    const allTouched = {
      firstName: true,
      middleName: true,
      lastName: true,
      emiratesId:
        registrationType === REGISTRATION_TYPE.EMIRATES_ID ? true : false,
      passportNumber: true,
      nationality: true,
    };
    setTouched(allTouched);

    const firstNameValid = validateField("firstName", firstName);
    const middleNameValid = validateField("middleName", middleName);
    const lastNameValid = validateField("lastName", lastName);
    const emiratesIdValid =
      registrationType === REGISTRATION_TYPE.EMIRATES_ID
        ? validateField("emiratesId", emiratesId)
        : true;
    const passportNumberValid = validateField("passportNumber", passportNumber);
    const nationalityValid = validateField("nationality", selectedNationality);

    return (
      firstNameValid &&
      middleNameValid &&
      lastNameValid &&
      emiratesIdValid &&
      passportNumberValid &&
      nationalityValid
    );
  };

  // Check if form is valid (for button state)
  // Middle name is optional, so we don't check it here
  const isFormValid =
    firstName.trim() !== "" &&
    lastName.trim() !== "" &&
    (registrationType === REGISTRATION_TYPE.PASSPORT ||
      emiratesId.trim() !== "") &&
    passportNumber.trim() !== "" &&
    selectedNationality !== undefined &&
    isTermsAccepted;

  const handleContinuePress = async () => {
    // If registration is already successful, navigate to document capture
    if (isRegistrationSuccessful) {
      logger.info(
        "RegistrationFormScreen",
        "Registration already successful, navigating to document-capture",
      );
      router.push("/document-upload");
      return;
    }

    if (!registrationType) {
      logger.error("RegistrationFormScreen", "Registration type not set");
      trackDynatraceEvent({
        p_screen: "signup",
        p_sectionName: registrationSectionName,
        p_agencyType: agencyType,
        p_actionName: "Continue",
        p_failedMessage: "Registration type not set",
        eventType: "error",
      });
      return;
    }

    if (!validateAllFields()) {
      logger.debug("RegistrationFormScreen", "Validation failed", errors);
      trackDynatraceEvent({
        p_screen: "signup",
        p_sectionName: registrationSectionName,
        p_agencyType: agencyType,
        p_actionName: "Continue",
        p_failedMessage: "Validation failed",
        eventType: "error",
      });
      return;
    }

    try {
      setIsLoading(true);
      trackDynatraceEvent({
        p_screen: "signup",
        p_sectionName: registrationSectionName,
        p_agencyType: agencyType,
        p_actionName: "Continue",
        eventType: "action",
      });

      // If TNC is accepted, fetch the current published version
      let tncVersion: string | undefined;
      if (isTermsAccepted) {
        try {
          const { fetchTNCVersion } =
            await import("@/core/services/legalDocumentsService");
          logger.info(
            "RegistrationFormScreen",
            "TNC accepted, fetching current published version",
          );
          const version = await fetchTNCVersion();
          logger.info("RegistrationFormScreen", "TNC version fetched", {
            version,
          });
          if (version !== null) {
            tncVersion = String(version); // Convert to string
            logger.info(
              "RegistrationFormScreen",
              "TNC version fetched successfully",
              { version: tncVersion },
            );
          } else {
            logger.warn(
              "RegistrationFormScreen",
              "Failed to fetch TNC version, continuing without version",
            );
          }
        } catch (error) {
          logger.error(
            "RegistrationFormScreen",
            "Error fetching TNC version",
            error,
          );
          // Continue without version - don't block registration
        }
      }

      // Prepare form data for storage
      const formData = {
        firstName: firstName.trim(),
        middleName: middleName.trim(),
        lastName: lastName.trim(),
        ...(registrationType === REGISTRATION_TYPE.EMIRATES_ID && {
          emiratesId: emiratesId,
        }),
        passportNumber: passportNumber.trim(),
        nationality: selectedNationality!.nationality,
        ...(tncVersion !== undefined && { tnc_version: tncVersion }),
      };

      // Save to store before navigation (no API call)
      registrationStore.setRegistrationData({
        registrationType,
        step: REGISTRATION_STEP.PHONE_OTP, // Update step to phone-otp
        formData,
      });

      logger.info(
        "RegistrationFormScreen",
        "Form data saved, navigating to phone OTP",
        {
          registrationType,
          hasTncVersion: tncVersion !== undefined,
        },
      );

      // Navigate to phone OTP verification (registration API will be called after email OTP)
      router.push("/phone-otp-inline" as any);
    } catch (error: any) {
      logger.error("RegistrationFormScreen", "Failed to save form data", error);
      setErrorDialogMessage("Failed to save form data. Please try again.");
      setErrorDialogVisible(true);
      trackDynatraceEvent({
        p_screen: "signup",
        p_sectionName: registrationSectionName,
        p_agencyType: agencyType,
        p_actionName: "Continue",
        p_failedMessage: error?.message || "Failed to save form data",
        eventType: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.backgroundLevel1,
    },
    keyboardAvoidingView: {
      flex: 1,
    },
    scrollView: {
      flex: 1,
    },
    scrollContentContainer: {
      flexGrow: 1,
      // Dynamic padding will be applied via inline style
    },
    contentContainer: {
      paddingHorizontal: 16,
      paddingTop: 16,
      paddingBottom: 20,
      flex: 1,
    },
    headerSection: {
      alignItems: "flex-start",
      marginBottom: 40,
    },
    iconContainer: {
      marginBottom: 21,
    },
    titleContainer: {
      alignItems: "flex-start",
      marginBottom: 16,
    },
    descriptionContainer: {
      alignItems: "flex-start",
    },
    formSection: {
      gap: 24,
    },
    buttonSection: {
      paddingHorizontal: 16,
      paddingTop: 16,
      backgroundColor: colors.backgroundLevel1, // Ensure button section has background
      position: "absolute", // Fix button at bottom
      bottom: 0,
      left: 0,
      right: 0,
      zIndex: 10, // Ensure button stays above scroll content
    },
    errorText: {
      marginTop: 4,
    },
    termsSection: {
      flexDirection: "row",
      alignItems: "flex-start",
      gap: 8,
      marginTop: 8,
    },
    checkboxContainer: {
      paddingTop: 2, // Align checkbox with first line of text (line height 20px, checkbox 24px)
    },
    termsText: {
      flex: 1,
    },
    termsTextContent: {
      fontSize: 15,
      lineHeight: 20,
      includeFontPadding: false,
      textAlignVertical: "top",
    },
    termsTextRegular: {
      fontSize: 15,
      lineHeight: 20,
      color: colors.fontSecondary, // #4d4d4d
      fontFamily: "Matter",
      fontWeight: "400",
      includeFontPadding: false,
      textAlignVertical: "top",
    },
    termsLink: {
      fontSize: 15,
      lineHeight: 20,
      color: colors.fontPrimary, // #333333
      fontFamily: "Matter-SemiBold",
      fontWeight: "600",
      textDecorationLine: "underline",
      includeFontPadding: false,
      textAlignVertical: "top",
    },
  });

  return (
    <View style={styles.container}>
      <ProgressHeader
        progress={progress}
        showBackButton={true}
        onBackPress={handleBackPress}
        color={colors.fontPrimary}
        showLanguageSwitch={true}
        onLanguagePress={() =>
          logger.debug("RegistrationFormScreen", "Language switch pressed")
        }
      />

      <KeyboardAvoidingView
        style={styles.keyboardAvoidingView}
        behavior={Platform.OS === "ios" ? "padding" : undefined}
        keyboardVerticalOffset={Platform.OS === "ios" ? 0 : 0}
        enabled={Platform.OS === "ios"} // Only enable on iOS, let Android handle it naturally
      >
        <ScrollView
          ref={scrollViewRef}
          style={styles.scrollView}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={[
            styles.scrollContentContainer,
            {
              // Increase padding when keyboard is visible to allow more scrolling
              paddingBottom:
                keyboardHeight > 0
                  ? keyboardHeight + 100 // Keyboard height + button area + extra padding
                  : 100, // Normal padding when keyboard is hidden (for fixed button)
            },
          ]}
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode="on-drag"
        >
          <View style={styles.contentContainer}>
            {/* Header Section */}
            <View style={styles.headerSection}>
              <View style={styles.iconContainer}>
                <HeartHandshakeIcon />
              </View>
              <View style={styles.titleContainer}>
                <Typography variant="title1" color="primary">
                  {t("registration.hello")}
                </Typography>
              </View>
              <View style={styles.descriptionContainer}>
                <Typography variant="body" color="secondary">
                  {t("registration.detailsDescription")}
                </Typography>
              </View>
            </View>

            {/* Form Section */}
            <View style={styles.formSection}>
              {/* First Name */}
              <View onLayout={(e) => handleFieldLayout("firstName", e)}>
                <TextInput
                  ref={firstNameRef}
                  label={t("registration.firstName")}
                  placeholder={t("registration.enterHere")}
                  value={firstName}
                  onChangeText={(text) => {
                    setFirstName(text);
                    if (touched.firstName) {
                      validateField("firstName", text);
                    }
                  }}
                  onFocus={() => {
                    handleFieldFocus("firstName");
                  }}
                  onBlur={() => {
                    setTouched((prev) => ({ ...prev, firstName: true }));
                    validateField("firstName", firstName);
                  }}
                  {...(touched.firstName && errors.firstName
                    ? { error: errors.firstName }
                    : {})}
                  autoCapitalize="words"
                  textContentType="givenName"
                  returnKeyType="next"
                  onSubmitEditing={() => middleNameRef.current?.focus()}
                  required
                  disabled={areFieldsDisabled}
                />
              </View>

              {/* Middle Name */}
              <View onLayout={(e) => handleFieldLayout("middleName", e)}>
                <TextInput
                  ref={middleNameRef}
                  label={t("registration.middleName")}
                  placeholder={t("registration.enterHere")}
                  value={middleName}
                  onChangeText={(text) => {
                    setMiddleName(text);
                    if (touched.middleName) {
                      validateField("middleName", text);
                    }
                  }}
                  onFocus={() => {
                    handleFieldFocus("middleName");
                  }}
                  onBlur={() => {
                    setTouched((prev) => ({ ...prev, middleName: true }));
                    validateField("middleName", middleName);
                  }}
                  {...(touched.middleName && errors.middleName
                    ? { error: errors.middleName }
                    : {})}
                  autoCapitalize="words"
                  textContentType="middleName"
                  returnKeyType="next"
                  onSubmitEditing={() => lastNameRef.current?.focus()}
                  disabled={areFieldsDisabled}
                />
              </View>

              {/* Last Name */}
              <View onLayout={(e) => handleFieldLayout("lastName", e)}>
                <TextInput
                  ref={lastNameRef}
                  label={t("registration.lastName")}
                  placeholder={t("registration.enterHere")}
                  value={lastName}
                  onChangeText={(text) => {
                    setLastName(text);
                    if (touched.lastName) {
                      validateField("lastName", text);
                    }
                  }}
                  onFocus={() => {
                    handleFieldFocus("lastName");
                  }}
                  onBlur={() => {
                    setTouched((prev) => ({ ...prev, lastName: true }));
                    validateField("lastName", lastName);
                  }}
                  {...(touched.lastName && errors.lastName
                    ? { error: errors.lastName }
                    : {})}
                  autoCapitalize="words"
                  textContentType="familyName"
                  returnKeyType="next"
                  onSubmitEditing={() => {
                    // Skip to passport if Emirates ID is hidden, otherwise focus Emirates ID
                    if (registrationType === REGISTRATION_TYPE.PASSPORT) {
                      passportNumberRef.current?.focus();
                    } else {
                      emiratesIdRef.current?.focus();
                    }
                  }}
                  required
                  disabled={areFieldsDisabled}
                />
              </View>

              {/* Emirates ID Number - Only show for UAE flow */}
              {registrationType === REGISTRATION_TYPE.EMIRATES_ID && (
                <View onLayout={(e) => handleFieldLayout("emiratesId", e)}>
                  <TextInput
                    ref={emiratesIdRef}
                    label={t("registration.emiratesIdNumber")}
                    placeholder={t("registration.enterHere")}
                    value={emiratesId}
                    onChangeText={(text) => {
                      // Format the input as user types or pastes
                      const formatted = formatEmiratesId(text);
                      setEmiratesId(formatted);
                      if (touched.emiratesId) {
                        validateField("emiratesId", formatted);
                      }
                    }}
                    onFocus={() => {
                      handleFieldFocus("emiratesId");
                    }}
                    onBlur={() => {
                      setTouched((prev) => ({ ...prev, emiratesId: true }));
                      validateField("emiratesId", emiratesId);
                    }}
                    {...(touched.emiratesId && errors.emiratesId
                      ? { error: errors.emiratesId }
                      : {})}
                    keyboardType="numeric"
                    returnKeyType="next"
                    onSubmitEditing={() => passportNumberRef.current?.focus()}
                    maxLength={18} // XXX-XXXX-XXXXXXX-X format (15 digits + 3 dashes)
                    required
                    leftIcon={<IdCardIcon size={16} />}
                    disabled={areFieldsDisabled}
                  />
                </View>
              )}

              {/* Passport Number */}
              <View onLayout={(e) => handleFieldLayout("passportNumber", e)}>
                <TextInput
                  ref={passportNumberRef}
                  label={t("registration.passportNumber")}
                  placeholder={t("registration.enterHere")}
                  value={passportNumber}
                  onChangeText={(text) => {
                    setPassportNumber(text);
                    if (touched.passportNumber) {
                      validateField("passportNumber", text);
                    }
                  }}
                  onFocus={() => {
                    handleFieldFocus("passportNumber");
                  }}
                  onBlur={() => {
                    setTouched((prev) => ({ ...prev, passportNumber: true }));
                    validateField("passportNumber", passportNumber);
                  }}
                  {...(touched.passportNumber && errors.passportNumber
                    ? { error: errors.passportNumber }
                    : {})}
                  autoCapitalize="characters"
                  returnKeyType="next"
                  onSubmitEditing={() => {
                    Keyboard.dismiss();
                  }}
                  required
                  leftIcon={<IdCardIcon size={16} />}
                  disabled={areFieldsDisabled}
                />
              </View>

              {/* Nationality */}
              <View onLayout={(e) => handleFieldLayout("nationality", e)}>
                <NationalityDropdown
                  label={t("registration.nationality")}
                  selectedNationality={selectedNationality}
                  onNationalitySelect={(nationality) => {
                    setSelectedNationality(nationality);
                    setTouched((prev) => ({ ...prev, nationality: true }));
                    validateField("nationality", nationality);
                  }}
                  placeholder={t("registration.selectNationality")}
                  required
                  {...(touched.nationality && errors.nationality
                    ? { error: errors.nationality }
                    : {})}
                  disabled={areFieldsDisabled}
                />
              </View>

              {/* Terms and Conditions */}
              <View style={styles.termsSection}>
                <View style={styles.checkboxContainer}>
                  <Checkbox
                    isSelected={isTermsAccepted}
                    onPress={() => setIsTermsAccepted((prev) => !prev)}
                    size={24}
                    isDisabled={areFieldsDisabled}
                  />
                </View>
                <View style={styles.termsText}>
                  <RNText style={styles.termsTextContent}>
                    <RNText style={styles.termsTextRegular}>
                      {t("registration.termsPrefix")}{" "}
                    </RNText>
                    <RNText
                      style={styles.termsLink}
                      onPress={() => router.push("/terms-and-conditions")}
                    >
                      {t("registration.termsLink")}
                    </RNText>
                    <RNText
                      style={styles.termsTextRegular}
                    >{` ${t("registration.andText")} `}</RNText>
                    <RNText
                      style={styles.termsLink}
                      onPress={() => router.push("/privacy-policy")}
                    >
                      {t("registration.privacyLink")}
                    </RNText>
                  </RNText>
                </View>
              </View>
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>

      {/* Button Section - Outside KeyboardAvoidingView for proper bottom alignment */}
      <View
        style={[
          styles.buttonSection,
          { paddingBottom: Math.max(insets.bottom, 45) },
        ]}
      >
        <Button
          title={isLoading ? t("common.loading") : t("common.continue")}
          variant="primary"
          fullWidth={true}
          onPress={handleContinuePress}
          disabled={isLoading || (!isRegistrationSuccessful && !isFormValid)}
        />
      </View>

      {/* Error Dialog */}
      <OkDialog
        visible={errorDialogVisible}
        message={errorDialogMessage}
        onOk={() => {
          setErrorDialogVisible(false);
        }}
      />
    </View>
  );
}

export default RegistrationFormScreen;
